import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function PrivacyPolicy() {
  const lastUpdated = 'January 23, 2026';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-900 via-surface-950 to-primary-950">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/register" 
            className="inline-flex items-center gap-2 text-surface-400 hover:text-primary-400 transition-colors mb-6"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Registration
          </Link>
          <h1 className="text-4xl font-display font-bold text-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-surface-400">Last updated: {lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          <div className="bg-surface-900/80 backdrop-blur-xl rounded-2xl border border-surface-800 p-8 space-y-8">
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="text-surface-300 leading-relaxed">
                Neoshift BI, operated by PT Neo Teknologi Perkasa ("we," "our," or "us"), is committed to protecting 
                your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our business intelligence platform and related services (collectively, the "Service").
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                By using the Service, you consent to the data practices described in this policy. If you do not 
                agree with this policy, please do not use the Service.
              </p>
              <p className="text-surface-300 leading-relaxed mt-4">
                <strong>Legal Entity:</strong> PT Neo Teknologi Perkasa
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-white mt-6 mb-3">2.1 Account Information</h3>
              <p className="text-surface-300 leading-relaxed">
                When you register for an account, we collect:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Name (first and last name)</li>
                <li>Email address</li>
                <li>Company name and industry</li>
                <li>Billing email address</li>
                <li>Password (stored in encrypted form)</li>
              </ul>

              <h3 className="text-xl font-medium text-white mt-6 mb-3">2.2 Your Business Data</h3>
              <p className="text-surface-300 leading-relaxed">
                You may upload or connect various data sources to the Service. This data may include:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Business metrics and analytics data</li>
                <li>Database contents and queries</li>
                <li>Files (CSV, Excel, etc.) you upload</li>
                <li>Data from connected third-party services</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                You retain full ownership of your business data. We process it only to provide the Service.
              </p>

              <h3 className="text-xl font-medium text-white mt-6 mb-3">2.3 Usage Information</h3>
              <p className="text-surface-300 leading-relaxed">
                We automatically collect information about how you use the Service:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Log data (IP address, browser type, access times)</li>
                <li>Feature usage and interaction patterns</li>
                <li>Error reports and performance data</li>
                <li>AI feature usage and credit consumption</li>
              </ul>

              <h3 className="text-xl font-medium text-white mt-6 mb-3">2.4 Payment Information</h3>
              <p className="text-surface-300 leading-relaxed">
                Payment processing is handled by secure third-party payment processors. We do not store 
                complete credit card numbers on our servers. We may receive limited payment information 
                (such as the last four digits of your card) for reference purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-surface-300 leading-relaxed">We use the collected information to:</p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Provide, maintain, and improve the Service</li>
                <li>Process your transactions and manage your account</li>
                <li>Send administrative communications (service updates, security alerts)</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
                <li>Send marketing communications (with your consent, and you can opt out)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. AI and Machine Learning</h2>
              <p className="text-surface-300 leading-relaxed">
                Our Service includes AI-powered features that process your data to provide insights, 
                generate charts, and answer natural language queries. Important points:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>AI processing is performed to serve your requests only</li>
                <li>We do not use your business data to train our AI models</li>
                <li>AI queries may be processed by third-party AI providers under strict confidentiality agreements</li>
                <li>You can view AI credit usage in your account settings</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-surface-300 leading-relaxed">
                We do not sell your personal information or business data. We may share information in 
                the following circumstances:
              </p>
              
              <h3 className="text-xl font-medium text-white mt-6 mb-3">5.1 Service Providers</h3>
              <p className="text-surface-300 leading-relaxed">
                We work with third-party service providers who assist in operating the Service:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Cloud infrastructure providers (data hosting)</li>
                <li>Payment processors (billing)</li>
                <li>AI service providers (AI features)</li>
                <li>Email service providers (transactional emails)</li>
                <li>Analytics tools (anonymized usage data)</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                These providers are bound by confidentiality obligations and can only use your data 
                to provide services to us.
              </p>

              <h3 className="text-xl font-medium text-white mt-6 mb-3">5.2 Legal Requirements</h3>
              <p className="text-surface-300 leading-relaxed">
                We may disclose information if required by law, regulation, or legal process, or if we 
                believe disclosure is necessary to protect our rights, your safety, or the safety of others.
              </p>

              <h3 className="text-xl font-medium text-white mt-6 mb-3">5.3 Business Transfers</h3>
              <p className="text-surface-300 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be 
                transferred as part of that transaction. We will notify you of any such change.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
              <p className="text-surface-300 leading-relaxed">
                We implement comprehensive security measures to protect your data:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest (AES-256)</li>
                <li><strong>Access Controls:</strong> Role-based access with multi-factor authentication options</li>
                <li><strong>Infrastructure:</strong> Enterprise-grade cloud infrastructure with SOC 2 compliance</li>
                <li><strong>Monitoring:</strong> Automated uptime monitoring with email alerts for service disruptions</li>
                <li><strong>Backups:</strong> Database backups via MongoDB Atlas</li>
                <li><strong>Auditing:</strong> Infrastructure access logging via Google Cloud Audit Logs</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                While we strive to protect your data, no method of transmission or storage is 100% secure. 
                We cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention</h2>
              <p className="text-surface-300 leading-relaxed">
                We retain your data for as long as your account is active or as needed to provide the Service. 
                After account termination:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>You have 30 days to export your data</li>
                <li>After 30 days, your business data is permanently deleted</li>
                <li>Account information may be retained for up to 90 days for legal compliance</li>
                <li>Aggregated, anonymized data may be retained indefinitely for analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Your Rights and Choices</h2>
              <p className="text-surface-300 leading-relaxed">
                Depending on your jurisdiction, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data</li>
                <li><strong>Portability:</strong> Request your data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                To exercise these rights, contact us at the email address below. We will respond within 
                30 days (or as required by applicable law).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Cookies and Tracking</h2>
              <p className="text-surface-300 leading-relaxed">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Maintain your session and authentication state</li>
                <li>Remember your preferences</li>
                <li>Analyze usage patterns (with anonymized data)</li>
                <li>Improve the Service</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                Essential cookies are required for the Service to function. You can control non-essential 
                cookies through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. International Data Transfers</h2>
              <p className="text-surface-300 leading-relaxed">
                Your data may be processed in countries other than your own. We ensure appropriate 
                safeguards are in place for international transfers, including:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Standard Contractual Clauses approved by relevant authorities</li>
                <li>Adequacy decisions for certain jurisdictions</li>
                <li>Binding Corporate Rules where applicable</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Children's Privacy</h2>
              <p className="text-surface-300 leading-relaxed">
                The Service is not intended for individuals under the age of 18. We do not knowingly 
                collect personal information from children. If we become aware that we have collected 
                personal information from a child, we will take steps to delete it.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
              <p className="text-surface-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material 
                changes by:
              </p>
              <ul className="list-disc list-inside text-surface-300 space-y-2 mt-4">
                <li>Posting the updated policy on our website</li>
                <li>Sending an email notification to your registered email address</li>
                <li>Displaying a notice within the Service</li>
              </ul>
              <p className="text-surface-300 leading-relaxed mt-4">
                Your continued use of the Service after changes take effect constitutes acceptance 
                of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
              <p className="text-surface-300 leading-relaxed">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-4 space-y-2 text-surface-300">
                <p><strong>Email:</strong> <span className="text-primary-400">support@bi.neoshift.ai</span></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Jurisdiction-Specific Rights</h2>
              
              <h3 className="text-xl font-medium text-white mt-6 mb-3">For California Residents (CCPA)</h3>
              <p className="text-surface-300 leading-relaxed">
                California residents have additional rights under the CCPA, including the right to know 
                what personal information is collected, the right to delete, and the right to opt-out of 
                the sale of personal information. We do not sell personal information.
              </p>

              <h3 className="text-xl font-medium text-white mt-6 mb-3">For European Residents (GDPR)</h3>
              <p className="text-surface-300 leading-relaxed">
                If you are in the European Economic Area, you have rights under the GDPR including access, 
                rectification, erasure, restriction, portability, and the right to lodge a complaint with 
                a supervisory authority.
              </p>
            </section>

          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-surface-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Neoshift BI. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
